import {
  calcularTotalCompraCentavos,
  pesoAMilesimas,
  precioACentavos,
  taraAMilesimas,
} from './compras'
import { normalizarMoneda } from './numeros'

// Reglas de Abastecimiento (compra PREVIA/DIRECTA, recepción y factura).
// Supabase es la autoridad final de permisos y montos; esto solo decide
// qué mostrar/habilitar y valida los formularios antes de llamar a las RPC.

export const MODALIDADES = {
  PREVIA: {
    etiqueta: 'Previa',
    clase: 'bg-sky-100 text-sky-700',
  },
  DIRECTA: {
    etiqueta: 'Directa',
    clase: 'bg-slate-100 text-slate-700',
  },
}

export function obtenerModalidad(valor) {
  return MODALIDADES[valor] || MODALIDADES.DIRECTA
}

export const ESTADOS_RECEPCION = {
  BORRADOR: {
    etiqueta: 'Recepción en borrador',
    clase: 'bg-amber-100 text-amber-700',
  },
  VALIDADA: {
    etiqueta: 'Recepción validada',
    clase: 'bg-emerald-100 text-emerald-700',
  },
  RECHAZADA_TOTAL: {
    etiqueta: 'Rechazada totalmente',
    clase: 'bg-red-50 text-red-600',
  },
}

export const ESTADO_SIN_RECEPCION = {
  etiqueta: 'Sin recepción',
  clase: 'bg-slate-100 text-slate-600',
}

export function obtenerEstadoRecepcion(recepcion) {
  if (!recepcion) return ESTADO_SIN_RECEPCION

  return ESTADOS_RECEPCION[recepcion.estado] || ESTADO_SIN_RECEPCION
}

// ---------- Permisos (matriz aprobada, regla 17) ----------

const ROLES_REGISTRAR = ['administrador', 'aprobador', 'usuario']
const ROLES_VALIDAR = ['administrador', 'aprobador']

export function puedeRegistrarCompra(rol) {
  return ROLES_REGISTRAR.includes(rol)
}

export function puedeRegistrarRecepcion(rol) {
  return ROLES_REGISTRAR.includes(rol)
}

export function puedeValidarRecepcion(rol) {
  return ROLES_VALIDAR.includes(rol)
}

export function puedeRegistrarFactura(rol) {
  return ROLES_VALIDAR.includes(rol)
}

export function puedeCorregirRecepcion(rol) {
  return rol === 'administrador'
}

// ---------- Estado de la compra / recepción ----------

// Una compra histórica (recepcion_automatica_habilitada = false) nunca
// debe tratarse como recepción pendiente de ingreso automático.
export function compraAdmiteRecepcion(compra, recepcion) {
  if (!compra) return false
  if (compra.estado === 'anulada') return false
  if (compra.recepcion_automatica_habilitada === false) return false

  return !recepcion || recepcion.estado === 'BORRADOR'
}

// El monto de una compra no es definitivo hasta validar la recepción:
// PREVIA muestra el estimado (kg acordados × precio) y DIRECTA el
// provisional (neto × precio). Tras validar es kg aceptados × precio.
export function obtenerMontoCompra(compra, recepcion) {
  const validada = recepcion?.estado === 'VALIDADA'
  const rechazada = recepcion?.estado === 'RECHAZADA_TOTAL'

  if (validada || rechazada) {
    return {
      valor: Number(compra.monto_logistico_registrado ?? 0),
      etiqueta: 'Total definitivo',
      definitivo: true,
    }
  }

  if (compra.modalidad === 'PREVIA') {
    return {
      valor: Number(compra.monto_estimado ?? 0),
      etiqueta: 'Monto estimado',
      definitivo: false,
    }
  }

  return {
    valor: Number(compra.monto_logistico_registrado ?? 0),
    etiqueta: 'Monto provisional',
    definitivo: false,
  }
}

export function puedeAnularAbastecimiento(compra, recepcion) {
  if (!compra) return false
  if (compra.estado === 'anulada') return false

  // Con cualquier recepción (incluso en borrador) el backend exige
  // revisión antes de anular, así que la acción se oculta.
  return !recepcion
}

// Traduce errores técnicos de Postgres a mensajes entendibles.
export function mensajeErrorFactura(error) {
  const mensaje = error?.message || ''

  if (mensaje.includes('facturas_proveedor_compras_compra_id_key')) {
    return 'Alguna de las compras seleccionadas ya está asociada a otra factura.'
  }

  if (mensaje.includes('ux_gastos_factura_proveedor_documento_f2')) {
    return 'Esta factura ya existe en Gastos; revisarla para evitar duplicación.'
  }

  return mensaje || 'No fue posible registrar la factura.'
}

// ---------- Compra PREVIA ----------

// kilos acordados x precio/kg, en pesos (misma fórmula entera que el
// resto del módulo). Devuelve null si algún dato no es válido.
export function calcularMontoEstimado(kilosAcordados, precioKilo) {
  const milesimas = pesoAMilesimas(kilosAcordados)
  const centavos = precioACentavos(precioKilo)

  if (!Number.isFinite(milesimas) || !Number.isFinite(centavos)) {
    return null
  }

  return calcularTotalCompraCentavos(milesimas, centavos) / 100
}

// Normaliza condición de pago + plazo para las RPC.
// CONTADO -> plazo null ; CRÉDITO -> días corridos enteros > 0.
export function validarCondicionPago(condicion, plazoTexto) {
  if (condicion === 'CONTADO') {
    return { condicion, plazo: null }
  }

  if (condicion !== 'CRÉDITO') {
    return { error: 'Debe seleccionar la condición de pago.' }
  }

  const texto = String(plazoTexto ?? '').trim()

  if (!/^\d+$/.test(texto) || Number(texto) < 1 || Number(texto) > 36500) {
    return {
      error: 'El plazo de crédito debe ser un número entero de días mayor a cero.',
    }
  }

  return { condicion, plazo: Number(texto) }
}

export function validarCompraPrevia({
  proveedorId,
  fecha,
  productoId,
  kilosAcordados,
  precioKilo,
  condicion,
  plazoDias,
}) {
  if (!proveedorId) return { error: 'Debe seleccionar un proveedor.' }
  if (!fecha) return { error: 'Debe ingresar la fecha.' }
  if (!productoId) return { error: 'Debe seleccionar un producto.' }

  const milesimas = pesoAMilesimas(kilosAcordados)

  if (!Number.isFinite(milesimas) || milesimas <= 0) {
    return {
      error:
        'Kilos acordados no válidos. Usa coma o punto decimal, máximo 3 decimales, mayor a cero.',
    }
  }

  const centavos = precioACentavos(precioKilo)

  if (!Number.isFinite(centavos) || centavos <= 0) {
    return {
      error:
        'Precio/kg no válido. Ejemplos: 750, 2.500 o 750,50 (máximo 2 decimales).',
    }
  }

  const pago = validarCondicionPago(condicion, plazoDias)

  if (pago.error) return { error: pago.error }

  return {
    valores: {
      kilos: milesimas / 1000,
      precio: centavos / 100,
      condicion: pago.condicion,
      plazo: pago.plazo,
    },
  }
}

// ---------- Recepción ----------

// Valida kilos aceptados contra el neto recibido (en milésimas).
export function validarKilosAceptados({
  kilosAceptados,
  netoKg,
  motivoRechazo,
}) {
  const aceptados = pesoAMilesimas(kilosAceptados)

  if (!Number.isFinite(aceptados)) {
    return {
      error:
        'Kilos aceptados no válidos. Usa coma o punto decimal, máximo 3 decimales.',
    }
  }

  const neto = taraAMilesimas(netoKg)

  if (aceptados > neto) {
    return { error: 'Los kilos aceptados no pueden superar el peso neto recibido.' }
  }

  if (aceptados === 0 && !String(motivoRechazo ?? '').trim()) {
    return { error: 'Si no se acepta ningún kilo, debe indicar el motivo del rechazo.' }
  }

  return { kilos: aceptados / 1000, rechazoTotal: aceptados === 0 }
}

// Corrección de una recepción validada: positivo, ≤ neto, distinto del
// valor actual y con motivo. No permite pasar a 0 kg.
export function validarCorreccionRecepcion({
  kilosNuevos,
  kilosActuales,
  netoKg,
  motivo,
}) {
  const nuevos = pesoAMilesimas(kilosNuevos)

  if (!Number.isFinite(nuevos) || nuevos <= 0) {
    return {
      error:
        'Los kilos aceptados deben ser mayores a cero (máximo 3 decimales). Un rechazo total no se corrige desde acá.',
    }
  }

  if (nuevos > taraAMilesimas(netoKg)) {
    return { error: 'Los kilos aceptados no pueden superar el peso neto recibido.' }
  }

  if (nuevos === taraAMilesimas(kilosActuales)) {
    return { error: 'Los kilos indicados son iguales a los actuales.' }
  }

  if (!String(motivo ?? '').trim()) {
    return { error: 'Debe indicar el motivo de la corrección.' }
  }

  return { kilos: nuevos / 1000 }
}

// ---------- Factura de proveedor ----------

export const TIPOS_DOCUMENTO_FACTURA = [
  { valor: 'factura', etiqueta: 'Factura' },
  { valor: 'factura_exenta', etiqueta: 'Factura exenta' },
]

function montoOpcional(texto) {
  if (String(texto ?? '').trim() === '') return 0

  return normalizarMoneda(texto)
}

// Redondeo a centavos sin arrastre de float para sumar y comparar.
function aCentavos(valor) {
  return Math.round(valor * 100)
}

export function sumarMontosFactura({ neto, exento, iva, otros }) {
  const montos = [neto, exento, iva, otros].map(montoOpcional)

  if (montos.some((monto) => !Number.isFinite(monto) || monto < 0)) {
    return null
  }

  return montos.reduce((suma, monto) => suma + aCentavos(monto), 0) / 100
}

export function validarFactura({
  tipoDocumento,
  numeroDocumento,
  fechaFactura,
  condicion,
  plazoDias,
  neto,
  exento,
  iva,
  otros,
  total,
}) {
  if (!['factura', 'factura_exenta'].includes(tipoDocumento)) {
    return { error: 'Debe seleccionar el tipo de documento.' }
  }

  const numero = String(numeroDocumento ?? '').trim()

  if (!numero || numero.length > 100) {
    return { error: 'Debe ingresar el número de la factura (máximo 100 caracteres).' }
  }

  if (!fechaFactura) return { error: 'Debe ingresar la fecha de la factura.' }

  const pago = validarCondicionPago(condicion, plazoDias)

  if (pago.error) return { error: pago.error }

  const montos = {
    neto: montoOpcional(neto),
    exento: montoOpcional(exento),
    iva: montoOpcional(iva),
    otros: montoOpcional(otros),
    total: normalizarMoneda(total),
  }

  const invalido = Object.values(montos).some(
    (monto) => !Number.isFinite(monto) || monto < 0
  )

  if (invalido) {
    return { error: 'Revisa los montos: deben ser números válidos, sin negativos.' }
  }

  const conMasDecimales = Object.values(montos).some(
    (monto) => Math.abs(monto * 100 - Math.round(monto * 100)) > 1e-6
  )

  if (conMasDecimales) {
    return { error: 'Los montos admiten como máximo 2 decimales.' }
  }

  if (montos.total <= 0) {
    return { error: 'El total de la factura debe ser mayor a cero.' }
  }

  const suma = sumarMontosFactura({ neto, exento, iva, otros })

  if (aCentavos(suma) !== aCentavos(montos.total)) {
    return {
      error: 'El total debe ser igual a neto + exento + IVA + otros impuestos.',
    }
  }

  return {
    valores: {
      tipoDocumento,
      numero,
      fechaFactura,
      condicion: pago.condicion,
      plazo: pago.plazo,
      ...montos,
    },
  }
}

// Vencimiento = fecha factura + plazo (días corridos). Solo informativo:
// el vencimiento real lo calcula la RPC.
export function calcularVencimientoFactura(fechaFactura, plazoDias) {
  if (!fechaFactura || !Number.isInteger(plazoDias)) return null

  const [anio, mes, dia] = fechaFactura.split('-').map(Number)
  const fecha = new Date(Date.UTC(anio, mes - 1, dia + plazoDias))

  return fecha.toISOString().slice(0, 10)
}

// Clasifica la selección de compras para registrar una factura.
// Devuelve { error } si no se puede continuar, o { ok: true }.
export function evaluarSeleccionFactura(compras, recepcionesPorCompra) {
  if (compras.length === 0) {
    return { error: 'Selecciona al menos una compra.' }
  }

  const proveedores = new Set(compras.map((compra) => compra.proveedor_id))

  if (proveedores.size > 1) {
    return { error: 'Todas las compras deben ser del mismo proveedor.' }
  }

  const rechazadas = compras.filter(
    (compra) => recepcionesPorCompra.get(compra.id)?.estado === 'RECHAZADA_TOTAL'
  )

  if (rechazadas.length > 0) {
    return {
      revisionAdministrativa: true,
      error:
        rechazadas.length === compras.length
          ? 'La recepción fue rechazada totalmente: esta factura requiere revisión administrativa en Gastos. No se genera gasto automático.'
          : 'La selección mezcla compras aceptadas con compras rechazadas totalmente: la factura requiere revisión administrativa en Gastos. No se automatiza.',
    }
  }

  const sinValidar = compras.filter(
    (compra) => recepcionesPorCompra.get(compra.id)?.estado !== 'VALIDADA'
  )

  if (sinValidar.length > 0) {
    return { error: 'Solo se pueden facturar compras con recepción validada.' }
  }

  return { ok: true }
}

export function puedeSeleccionarParaFactura(compra, recepcion, facturada) {
  return (
    compra.estado !== 'anulada' &&
    compra.recepcion_automatica_habilitada !== false &&
    !facturada &&
    ['VALIDADA', 'RECHAZADA_TOTAL'].includes(recepcion?.estado)
  )
}
